import { Typography, Box, Grid, Stack } from "@mui/material";
import StandardButton from "../../../../components/StandardButton";
import React from "react";

import useQuestions from "../../../../hooks/useQuestions";

import MightyDataGrid from "../../../../components/organisms/MightyDataGrid";
import QuestionEditDialog from "./QuestionEditDialog";
import { DataTypeString } from "../../../../data/GlobalData";
import ReorderDialog from "./ReorderDialog";
import EmptyReportQuestions from "./EmptyReportQuestions";
import EmptyDailyQuestions from "./EmptyDailyQuestions";

const editQuestions = ({ type }) => {
  const { questions, loading, saveQuestions, addQuestion, deleteQuestion } =
    useQuestions(type);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [questionReorderDialogOpen, setQuestionReorderDialogOpen] =
    React.useState(false);

  const selectedQuestion = React.useRef();

  const onEdit = (item) => {
    selectedQuestion.current = item;
    setDialogOpen(true);
  };

  const onDelete = (item) => {
    deleteQuestion(item.id);
  };

  const titleStrings = React.useMemo(() => {
    let button = "unknown";
    let title = "unknown";
    let editTitle = "unknown";
    let addTitle = "unknown";
    let reorderTitle = "unknown";
    let description = "unknown";

    switch (type) {
      case "weekly":
        button = "Add Report Question";
        title = "Report Questions";
        addTitle = "Add Report Question";
        editTitle = "Edit Report Question";
        reorderTitle = "Reorder Report Questions";
        description =
          "Report questions are asked when your client submits a report to the coach.";
        break;
      case "daily":
        button = "Add Daily Question";
        title = "Daily Report Questions";
        addTitle = "Add Daily Question";
        editTitle = "Edit Daily Question";
        reorderTitle = "Reorder Daily Questions";
        description =
          "Daily questions are a part of the daily report submitted by your client using the Reports tab in the Mighty45 app.";
        break;
      default:
        button = "unknown";
        title = "unknown";
        description = "unknown";
        addTitle = "unknown";
        editTitle = "unknown";
        reorderTitle = "unknown";
        break;
    }
    return { button, title, description, editTitle, addTitle, reorderTitle };
  }, [type]);

  // Memoize the getTierString function to prevent recreation
  const getTierString = React.useCallback((data) => {
    let tiers = "";
    if (data.tier1 && data.tier2 && data.tier3) {
      return "All tiers";
    }

    if (data.tier1 || data.tier1 === undefined) {
      tiers = tiers.concat("Tier 1 ");
    }
    if (data.tier2 || data.tier2 === undefined) {
      tiers = tiers.concat("Tier 2 ");
    }
    if (data.tier3 || data.tier3 === undefined) {
      tiers = tiers.concat("Tier 3 ");
    }
    return tiers.trim();
  }, []);

  const columns = React.useMemo(() => {
    return [
      {
        field: "name",
        headerName: "Name",
        headerAlign: "left",
        align: "left",
        width: 400, // Larger fixed width instead of flex to prevent shaking
        type: "string",
        hideable: false,
        sortable: false,
        filterable: false,
      },
      {
        field: "type",
        headerName: "Type",
        width: 150,
        sortable: false,
        valueGetter: (params) => {
          return DataTypeString[params.row.type];
        },
      },
      {
        field: "minimum",
        headerName: "Minimum",
        width: 120,
        sortable: false,
        valueGetter: (params) => {
          if (!params.row.minValue) {
            return "-";
          } else {
            return params.row.minValue;
          }
        },
      },
      {
        field: "maximum",
        headerName: "Maximum",
        width: 120,
        sortable: false,
        valueGetter: (params) => {
          if (!params.row.maxValue) {
            return "-";
          } else {
            return params.row.maxValue;
          }
        },
      },
      {
        field: "tiers",
        headerName: "Tiers",
        width: 150,
        sortable: false,
        valueGetter: (params) => {
          return getTierString(params.row);
        },
      },
      {
        field: "required",
        headerName: "Required?",
        width: 120,
        sortable: false,
        type: "singleSelect",
        valueOptions: [true, false],
        valueGetter: (params) => {
          return params.row.isRequired;
        },
      },
    ];
  }, [getTierString]);

  return (
    <>
      {/* Show empty state only after loading and when there are no questions */}
      {loading ? (
        <Box style={{ margin: 24, textAlign: "center" }}>
          <Typography>Loading...</Typography>
        </Box>
      ) : !questions || questions.length === 0 ? (
        <>
          {type === "weekly" ? (
            <EmptyReportQuestions
              onAddQuestion={() => {
                selectedQuestion.current = null;
                setDialogOpen(true);
              }}
            />
          ) : type === "daily" ? (
            <EmptyDailyQuestions
              onAddQuestion={() => {
                selectedQuestion.current = null;
                setDialogOpen(true);
              }}
            />
          ) : null}
        </>
      ) : (
        <>
          <Box style={{ marginBottom: 54 }}>
            <Grid container style={{ marginTop: 4 }}>
              <Grid item xs={7}>
                <Typography
                  style={{ fontSize: 20, fontWeight: "500", marginBottom: 12 }}
                >
                  {titleStrings.title}
                </Typography>
                <Typography
                  style={{
                    fontSize: 16,
                    fontWeight: "normal",
                    marginBottom: 12,
                  }}
                >
                  {titleStrings.description}
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Stack
                  direction={"row"}
                  spacing={3}
                  justifyContent={"flex-end"}
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent={"flex-end"}
                  >
                    <StandardButton
                      style={{ marginTop: 12 }}
                      variant="primary"
                      onClick={() => {
                        selectedQuestion.current = null;
                        setQuestionReorderDialogOpen(true);
                      }}
                    >
                      Reorder Questions
                    </StandardButton>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent={"flex-end"}
                  >
                    <StandardButton
                      style={{ marginTop: 12 }}
                      variant="primary"
                      onClick={() => {
                        selectedQuestion.current = null;
                        setDialogOpen(true);
                      }}
                    >
                      {titleStrings.button}
                    </StandardButton>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
            <Box style={{ margin: 12 }}>
              <MightyDataGrid
                entityName={`questions_${type}`}
                columns={columns}
                rows={questions}
                showActions={true}
                actionConfig={{
                  onEdit: onEdit,
                  onDelete: onDelete,
                  entityName: "Question",
                  pageName: "Admin",
                  tabName: "Questions",
                }}
                disableColumnResize={true}
              />
            </Box>
          </Box>
        </>
      )}

      {/* Dialogs - Always rendered regardless of empty state */}
      {dialogOpen && (
        <QuestionEditDialog
          open={dialogOpen}
          titleStrings={titleStrings}
          question={selectedQuestion.current}
          questionType={type}
          onDialogClosed={async (data, question) => {
            if (!data && !question) {
              setDialogOpen(false);
              return;
            }
            setDialogOpen(false);

            if (data && question) {
              question.name = data.name;
              question.subTitle = data.subTitle;
              question.minValue = data.minValue;
              question.maxValue = data.maxValue;
              question.isRequired = data.isRequired;
              question.tier1 = data.tier1;
              question.tier2 = data.tier2;
              question.tier3 = data.tier3;
              saveQuestions(questions);
            } else {
              addQuestion(
                data.name,
                data.subTitle,
                data.minValue,
                data.maxValue,
                data.isRequired,
                data.tier1,
                data.tier2,
                data.tier3,
                data.type,
              );
            }
          }}
        />
      )}
      {questionReorderDialogOpen && (
        <ReorderDialog
          open={questionReorderDialogOpen}
          reorderTitle={titleStrings.reorderTitle}
          list={questions}
          onDialogClosed={async (data) => {
            if (!data) {
              setQuestionReorderDialogOpen(false);
              return;
            }
            setQuestionReorderDialogOpen(false);
            if (data) {
              saveQuestions(data);
            }
          }}
          // onDialogClosed={(questions, list) => {
          //   if (list) {
          //     ProtocolData.setSplitOrder(protocolId, list);
          //   }
          //   setQuestionReorderDialogOpen(false);
          // }}
        />
      )}
    </>
  );
};
export default editQuestions;
